import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Reverse sync: when the person manually clicks/swipes to a different slide
    // (not a slide change we triggered ourselves from a color click), select the
    // matching color swatch too, so the two stay in sync both ways.
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideSelect, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Strips a Shopify CDN URL down to a stable, comparable filename fragment:
   * drops the query string (width/version/format params) and any trailing
   * "_{width}x{height}" size suffix some CDN paths embed before the extension.
   * @param {string} url
   * @returns {string}
   */
  #normalizeImageUrl(url) {
    if (!url) return '';
    const withoutQuery = url.split('?')[0];
    const filename = withoutQuery.split('/').pop() || '';
    return filename.replace(/_\d+x\d*(?=\.[a-zA-Z0-9]+$)/, '');
  }

  /**
   * Finds the index of the slide whose rendered image matches the given target URL.
   * @param {string} targetUrl
   * @returns {number} the matching slide index, or -1 if none found
   */
  #findSlideIndexByImageUrl(targetUrl) {
    const targetKey = this.#normalizeImageUrl(targetUrl);
    if (!targetKey) return -1;

    const slides = this.slideshow?.refs?.slides;
    if (!slides || !slides.length) return -1;

    return slides.findIndex((slide) => {
      const img = slide.querySelector('img');
      if (!img) return false;
      const src = img.currentSrc || img.src || img.getAttribute('src') || '';
      return this.#normalizeImageUrl(src) === targetKey;
    });
  }

  /**
   * Handles a variant update event by selecting the variant's media in slideshow.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const variantResource = event.detail.resource;

    if (!variantResource || !this.slideshow) {
      console.log('🖼️ Media Gallery: Missing variant resource or slideshow');
      return;
    }

    // Attempt 1: match by media ID. Shopify keeps two separate ID spaces for
    // "the same" image (a legacy Image ID vs a Media ID), and the slideshow's
    // slide-id attributes are keyed on the Media ID space. `featured_media_id`
    // is a custom field injected in the Liquid template using
    // `variant.featured_media.id`, which SHOULD be in the correct ID space —
    // but `variant.featured_media` can come back blank in some Liquid render
    // contexts, so this isn't guaranteed to be present.
    const featuredMediaId =
      variantResource.featured_media_id ?? variantResource.featured_media?.id ?? variantResource.featured_image?.id;

    const slides = this.slideshow.refs?.slides ?? [];
    const idMatchExists = featuredMediaId != null && slides.some((slide) => slide.getAttribute('slide-id') == featuredMediaId);

    if (idMatchExists) {
      console.log('🖼️ Media Gallery: Selecting by featured media ID:', featuredMediaId);
      this.slideshow.select({ id: featuredMediaId }, undefined, { animate: false });
      return;
    }

    // Attempt 2 (fallback): match by the variant's actual image URL. This is
    // reliable regardless of which ID space is populated, since every variant
    // has a real, working image URL.
    const featuredImageUrl = variantResource.featured_image_url;
    const slideIndex = this.#findSlideIndexByImageUrl(featuredImageUrl);

    if (slideIndex !== -1) {
      console.log('🖼️ Media Gallery: Selecting by image URL match, index:', slideIndex);
      this.slideshow.select(slideIndex, undefined, { animate: false });
    } else {
      console.log('🖼️ Media Gallery: No featured media ID or image URL match found', {
        featuredMediaId,
        featuredImageUrl,
      });
    }
  };

  /**
   * Handles a user-initiated slide selection (click, swipe, drag — anything
   * with a real triggering event, as opposed to our own programmatic syncs
   * from #handleVariantUpdate, which pass no event and so are never
   * "userInitiated"). Finds the color swatch whose `data-slide-id` matches
   * the newly selected slide and selects it, running the normal variant
   * change flow so price / URL / add-to-cart state all stay correct.
   *
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideSelect = (event) => {
    if (!event.detail?.userInitiated) return;

    const slideId = event.detail.id;
    if (!slideId) return;

    const sectionRoot = this.closest('.shopify-section, dialog');
    if (!sectionRoot) return;

    const colorInput = sectionRoot.querySelector(
      `variant-picker input[data-slide-id="${CSS.escape(String(slideId))}"]`
    );

    if (!(colorInput instanceof HTMLInputElement)) return;
    if (colorInput.checked) return; // already in sync, nothing to do

    colorInput.checked = true;
    colorInput.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
